/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.5.3 tnf_pavilion.glb -t -k -d -K -T 
Files: tnf_pavilion.glb [17.39MB] > /Users/pavelmazhuga/Sites/tnf-site/frontend/public/tnf_pavilion-transformed.glb [833.66KB] (95%)
*/
import {
    PerspectiveCamera as DreiPerspectiveCamera,
    Html,
    ScrollControls,
    useGLTF,
    useScroll,
    useTexture,
} from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import classNames from 'classnames';
import { damp } from 'maath/easing';
import React, { Dispatch, ReactNode, RefObject, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box3,
    Euler,
    Group,
    Mesh,
    MeshBasicMaterial,
    MeshStandardMaterial,
    PerspectiveCamera,
    SRGBColorSpace,
    Vector3,
} from 'three';
import { GLTF } from 'three-stdlib';
import { useMediaQuery } from 'usehooks-ts';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { usePavilionsValue } from '@/shared/atoms/pavilionsData';
import { useDragScroll } from '@/shared/hooks/use-drag-scroll';
import { useMounted } from '@/shared/hooks/use-mounted';
import Image from '../../shared/Image';
import { useMapStateContext } from '../InhouseMap/context';
import { STAND_POPUP_NAME } from '../StandPopup/StandPopup';
import { useStandPickerCameraViewValue } from '../atoms/stand-picker-camera-view';
import { useOnOutsideClickAction } from '../hooks/use-on-outside-click-action';
import positionsData from './data.json';

type GLTFResult = GLTF & {
    nodes: {
        box_p1: Mesh;
        box_p3: Mesh;
        box_p4: Mesh;
        box_p5: Mesh;
        box_p6: Mesh;
        box_p8: Mesh;
        box_p7: Mesh;
        box_s1: Mesh;
        box_s4: Mesh;
        box_s8: Mesh;
        box_s9: Mesh;
        box_s10: Mesh;
        box_s11: Mesh;
        box_s12: Mesh;
        box_s13: Mesh;
        box_s14: Mesh;
        box_s2: Mesh;
        box_s16: Mesh;
        box_s17: Mesh;
        box_s20: Mesh;
        box_s21: Mesh;
        box_s22: Mesh;
        box_s23: Mesh;
        box_s29: Mesh;
        box_i5: Mesh;
        box_i12: Mesh;
        box_i15: Mesh;
        box_i17: Mesh;
        box_i16: Mesh;
        box_i8: Mesh;
        box_p9: Mesh;
        box_p10: Mesh;
        box_p2: Mesh;
        box_s3: Mesh;
        box_s5: Mesh;
        box_s6: Mesh;
        box_s7: Mesh;
        box_s19: Mesh;
        box_s24: Mesh;
        box_s25: Mesh;
        box_s27: Mesh;
        box_s28: Mesh;
        box_s30: Mesh;
        box_i4: Mesh;
        box_i2: Mesh;
        box_i1: Mesh;
        box_i3: Mesh;
        box_i7: Mesh;
        box_i14: Mesh;
        box_i6: Mesh;
        box_i13: Mesh;
        box_s15: Mesh;
        box_s18: Mesh;
        box_s26: Mesh;
        box_i10: Mesh;
        box_i11: Mesh;
        box_i9: Mesh;
        Technopark002: Mesh;
        Technopark001: Mesh;
        EXPO_Lounge001: Mesh;
        EXPO_Lounge: Mesh;
    };
    materials: {};
};

const Wrapper = ({ children, meshRef }: { children: ReactNode; meshRef: RefObject<Mesh> }) => {
    const group = useRef<Group | null>(null);
    const meshWidth = useRef(0);
    const prevTime = useRef(0);
    const groupPosition = useRef(new Vector3());
    const scroll = useScroll();
    const isTablet = useMediaQuery('(max-width: 1024px)');
    const viewport = useThree((state) => state.viewport);
    const standPickerCameraView = useStandPickerCameraViewValue();

    useEffect(() => {
        if (meshRef.current && group.current) {
            const bbox = new Box3();
            bbox.setFromObject(group.current);

            if (bbox) {
                meshWidth.current =
                    (bbox.max.x - bbox.min.x) *
                        (standPickerCameraView === 'side' ? (isTablet ? 0.82 : 1.2) : isTablet ? 1.1 : 1.2) +
                    (isTablet ? -5 : 0);
            }
        }
    }, [meshRef, viewport.width, isTablet, standPickerCameraView]);

    useEffect(() => {
        if (group.current) {
            group.current.traverse((obj) => {
                if (obj !== group.current) {
                    obj.matrixAutoUpdate = false;
                }
            });
        }
    }, []);

    useFrame(({ clock }) => {
        const elapsedTime = clock.getElapsedTime();
        const delta = elapsedTime - prevTime.current;

        if (group.current && groupPosition.current) {
            const x = -Math.max(0, meshWidth.current - viewport.width) / (meshWidth.current / viewport.width) || 0;
            const k = (meshWidth.current - viewport.width) / 2;

            if (standPickerCameraView === 'side') {
                damp(
                    groupPosition.current,
                    'x',
                    x < 0 ? k - scroll.offset * (k * (isTablet ? 3.3 : 2.65)) : 0,
                    0.1,
                    delta,
                    undefined,
                    undefined,
                    eps,
                );
            } else {
                damp(
                    groupPosition.current,
                    'x',
                    x < 0 ? k - scroll.offset * (k * (isTablet ? 2 : 2)) + (isTablet ? 7 : 8) : 0,
                    0.1,
                    delta,
                    undefined,
                    undefined,
                    eps,
                );
            }

            group.current.position.x = groupPosition.current.x;
        }

        prevTime.current = elapsedTime;
    });

    return (
        <group ref={group} position={[0, isTablet ? -5 : -2, 0]}>
            {children}
        </group>
    );
};

type Props = JSX.IntrinsicElements['group'] & {
    markersPortalEl: RefObject<HTMLDivElement>;
    activePavilionSlug: string;
    setActivePavilionSlug: Dispatch<SetStateAction<string>>;
};

export const MODEL_URL = '/tnf-pavilion-3/tnf_pavilion_upd_4-transformed.glb';

/**
 * Чтобы с первого рендера заинитить useDragScroll,
 * рендерим этот компонент-хелпер условно
 */
const DragElInit = () => {
    const canvasEl = useThree((state) => state.gl.domElement);
    const scrollElRef = useRef<HTMLElement | null>(null);

    const scrollEl = canvasEl.nextElementSibling;
    if (scrollEl instanceof HTMLElement) {
        scrollElRef.current = scrollEl;
    }

    useDragScroll(scrollElRef);

    return null;
};

const eps = 0.0001;

function StandPicker({ markersPortalEl, activePavilionSlug, setActivePavilionSlug, ...props }: Props) {
    const { nodes } = useGLTF(MODEL_URL) as GLTFResult;
    const isTablet = useMediaQuery('(max-width: 1199px)');
    const camera = useRef<PerspectiveCamera | null>(null);
    const meshRef = useRef<Mesh>(null);
    const { openPopup } = useSetOpenedPopupsState();
    const { space, type, bookingType } = useMapStateContext();
    const isMounted = useMounted();
    const standPickerCameraView = useStandPickerCameraViewValue();
    const gl = useThree((state) => state.gl);

    const { pavilionData } = usePavilionsValue();
    const pavilionsData = pavilionData.data;
    const [
        bakedMap,
        expoLoungeMap,
        expoLoungeMaskMap,
        technoparkMap,
        technoparkMaskMap,
        streetMap,
        streetMaskMap,
        vhodMap,
        vhodMaskMap,
    ] = useTexture([
        isTablet
            ? '/tnf-pavilion-3/pavilion_CyclesBake_DIFFUSE_3-2k.webp'
            : '/tnf-pavilion-3/pavilion_CyclesBake_DIFFUSE_3.webp',
        '/tnf-pavilion-3/Expo-Lounge.png',
        '/tnf-pavilion-3/Expo-Lounge-mask.png',
        '/tnf-pavilion-3/Technopark.png',
        '/tnf-pavilion-3/Technopark-mask.png',
        '/tnf-pavilion-3/Street.png',
        '/tnf-pavilion-3/Street-mask.png',
        '/tnf-pavilion-3/Vhod.png',
        '/tnf-pavilion-3/Vhod-mask.png',
    ]);

    [
        bakedMap,
        expoLoungeMap,
        technoparkMap,
        streetMap,
        vhodMap,
        expoLoungeMaskMap,
        technoparkMaskMap,
        streetMaskMap,
        vhodMaskMap,
    ].forEach((map) => {
        map.colorSpace = SRGBColorSpace;
        map.flipY = false;
    });

    const vhodMaterial = useMemo(
        () => (nodes.EXPO_Lounge001.material as MeshStandardMaterial).clone(),
        [nodes.EXPO_Lounge001.material],
    );
    const expoLoungeMaterial = useMemo(
        () => (nodes.Technopark002.material as MeshStandardMaterial).clone(),
        [nodes.Technopark002.material],
    );
    const technoparkMaterial = useMemo(
        () => (nodes.Technopark001.material as MeshStandardMaterial).clone(),
        [nodes.Technopark001.material],
    );
    const streetMaterial = useMemo(
        () => (nodes.EXPO_Lounge.material as MeshStandardMaterial).clone(),
        [nodes.EXPO_Lounge.material],
    );

    useEffect(() => {
        if (technoparkMaterial instanceof MeshStandardMaterial) {
            technoparkMaterial.map = technoparkMap;
            technoparkMaterial.map.anisotropy = Math.min(4, gl.capabilities.getMaxAnisotropy());
            technoparkMaterial.alphaMap = technoparkMaskMap;
            technoparkMaterial.transparent = true;
        }

        if (expoLoungeMaterial instanceof MeshStandardMaterial) {
            expoLoungeMaterial.map = expoLoungeMap;
            expoLoungeMaterial.map.anisotropy = Math.min(4, gl.capabilities.getMaxAnisotropy());
            expoLoungeMaterial.alphaMap = expoLoungeMaskMap;
            expoLoungeMaterial.transparent = true;
        }

        if (streetMaterial instanceof MeshStandardMaterial) {
            streetMaterial.map = streetMap;
            streetMaterial.map.anisotropy = Math.min(4, gl.capabilities.getMaxAnisotropy());
            streetMaterial.alphaMap = streetMaskMap;
            streetMaterial.transparent = true;
        }

        if (vhodMaterial instanceof MeshStandardMaterial) {
            vhodMaterial.map = vhodMap;
            vhodMaterial.map.anisotropy = Math.min(4, gl.capabilities.getMaxAnisotropy());
            vhodMaterial.alphaMap = vhodMaskMap;
            vhodMaterial.transparent = true;
        }
    }, [
        expoLoungeMap,
        expoLoungeMaskMap,
        gl.capabilities,
        nodes.EXPO_Lounge.material,
        nodes.EXPO_Lounge001.material,
        nodes.Technopark001.material,
        nodes.Technopark002.material,
        streetMap,
        streetMaskMap,
        technoparkMap,
        technoparkMaskMap,
        vhodMap,
        vhodMaskMap,
        vhodMaterial,
        expoLoungeMaterial,
        technoparkMaterial,
        streetMaterial,
    ]);

    const pages = isTablet ? 3 : 1.5;

    useOnOutsideClickAction(
        ['.js-map-dropdown', '.js-pavilion', '.js-stand-popup', '.js-constructor-gallery-popup'],
        () => {
            setActivePavilionSlug('');
        },
    );

    useEffect(() => {
        document.dispatchEvent(new Event('stand-picker-mounted'));

        return () => {
            document.dispatchEvent(new Event('stand-picker-unmounted'));
        };
    }, []);

    const prevTime = useRef(0);

    const cameraPosition =
        standPickerCameraView === 'side'
            ? new Vector3(-92.427 + 20 + 15, 108.722 - 30 - 15 - 10, 160.581 - 40 - 20 - 20)
            : new Vector3(12, 175, isTablet ? -3 : 0);
    const cameraRotation =
        standPickerCameraView === 'side' ? new Euler(-0.588, -0.448, -0.281) : new Euler(-Math.PI / 2, 0, 0);

    const [initialCameraPosition] = useState(cameraPosition);
    const [initialCameraRotation] = useState(cameraRotation);
    const smoothTime = 0.5;
    const eps = 0.0001;

    useFrame(({ clock }) => {
        const elapsedTime = clock.getElapsedTime();
        const delta = elapsedTime - prevTime.current;

        if (camera.current) {
            damp(camera.current.position, 'x', cameraPosition.x, smoothTime, delta, undefined, undefined, eps);
            damp(camera.current.position, 'y', cameraPosition.y, smoothTime, delta, undefined, undefined, eps);
            damp(camera.current.position, 'z', cameraPosition.z, smoothTime, delta, undefined, undefined, eps);
            damp(camera.current.rotation, 'x', cameraRotation.x, smoothTime, delta, undefined, undefined, eps);
            damp(camera.current.rotation, 'y', cameraRotation.y, smoothTime, delta, undefined, undefined, eps);
            damp(camera.current.rotation, 'z', cameraRotation.z, smoothTime, delta, undefined, undefined, eps);
        }

        prevTime.current = elapsedTime;
    });

    const material = useMemo(() => new MeshBasicMaterial(), []);
    material.map = bakedMap;

    useEffect(() => {
        return () => {
            material.dispose();
        };
    }, [material]);

    return (
        <>
            {isMounted && <DragElInit />}
            <group {...props} dispose={null}>
                <group name="Map">
                    <DreiPerspectiveCamera
                        ref={camera}
                        name="Camera001"
                        makeDefault
                        far={500}
                        near={1.5}
                        fov={22.895}
                        position={initialCameraPosition}
                        rotation={initialCameraRotation}
                    />
                    <ScrollControls horizontal pages={pages}>
                        <Wrapper meshRef={meshRef}>
                            {/* --- */}
                            <mesh
                                ref={meshRef}
                                geometry={nodes.box_p1.geometry}
                                // material={nodes.box_p1.material}
                                position={[-33.58, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p3.geometry}
                                // material={nodes.box_p3.material}
                                position={[-15.673, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p4.geometry}
                                // material={nodes.box_p4.material}
                                position={[-5.914, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p5.geometry}
                                // material={nodes.box_p5.material}
                                position={[8.846, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p6.geometry}
                                // material={nodes.box_p6.material}
                                position={[17.383, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p8.geometry}
                                // material={nodes.box_p8.material}
                                position={[34.873, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p7.geometry}
                                // material={nodes.box_p7.material}
                                position={[25.92, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s1.geometry}
                                // material={nodes.box_s1.material}
                                position={[-47.677, 2.305, -13.195]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s4.geometry}
                                // material={nodes.box_s4.material}
                                position={[-47.677, 2.305, 0.512]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s8.geometry}
                                // material={nodes.box_s8.material}
                                position={[-44.422, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s9.geometry}
                                // material={nodes.box_s9.material}
                                position={[-41.142, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s10.geometry}
                                // material={nodes.box_s10.material}
                                position={[-37.862, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s11.geometry}
                                // material={nodes.box_s11.material}
                                position={[-34.58, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s12.geometry}
                                // material={nodes.box_s12.material}
                                position={[-31.3, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s13.geometry}
                                // material={nodes.box_s13.material}
                                position={[-28.018, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s14.geometry}
                                // material={nodes.box_s14.material}
                                position={[-24.737, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s15.geometry}
                                // material={nodes.box_s15.material}
                                position={[-19.815, 2.305, 13.639]}
                                rotation={[0, Math.PI / 2, 0]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s2.geometry}
                                // material={nodes.box_s2.material}
                                position={[-47.678, 2.305, -7.713]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s16.geometry}
                                // material={nodes.box_s16.material}
                                position={[-14.347, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s17.geometry}
                                // material={nodes.box_s17.material}
                                position={[-9.97, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s20.geometry}
                                // material={nodes.box_s20.material}
                                position={[10.749, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s21.geometry}
                                // material={nodes.box_s21.material}
                                position={[15.128, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s22.geometry}
                                // material={nodes.box_s22.material}
                                position={[19.505, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s23.geometry}
                                // material={nodes.box_s23.material}
                                position={[23.882, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s30.geometry}
                                // material={nodes.box_s30.material}
                                position={[54.509, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i5.geometry}
                                // material={nodes.box_i5.material}
                                position={[-20.503, 1.356, -1.716]}
                                scale={[1.026, 1, 1.026]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i15.geometry}
                                // material={nodes.box_i15.material}
                                position={[36.89, 2.635, -5.243]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i17.geometry}
                                // material={nodes.box_i17.material}
                                position={[43.916, 2.319, -0.244]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i16.geometry}
                                // material={nodes.box_i16.material}
                                position={[36.89, 2.635, 5.41]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i8.geometry}
                                // material={nodes.box_i8.material}
                                position={[-1.333, 2.635, -5.08]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i10.geometry}
                                // material={nodes.box_i10.material}
                                position={[-1.332, 2.635, 5.569]}
                                rotation={[0, 0, -Math.PI]}
                                scale={-1}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i11.geometry}
                                // material={nodes.box_i11.material}
                                position={[2.733, 2.635, 5.569]}
                                rotation={[-Math.PI, 0, -Math.PI]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i9.geometry}
                                // material={nodes.box_i9.material}
                                position={[2.731, 2.635, -5.08]}
                                rotation={[-Math.PI, 0, 0]}
                                scale={-1}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p9.geometry}
                                // material={nodes.box_p9.material}
                                position={[43.827, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p10.geometry}
                                // material={nodes.box_p10.material}
                                position={[52.78, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_p2.geometry}
                                // material={nodes.box_p2.material}
                                position={[-24.627, 2.305, -13.985]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s3.geometry}
                                // material={nodes.box_s3.material}
                                position={[-47.678, 2.305, -3.326]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s5.geometry}
                                // material={nodes.box_s5.material}
                                position={[-47.677, 2.305, 3.802]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s6.geometry}
                                // material={nodes.box_s6.material}
                                position={[-47.677, 2.305, 7.092]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s7.geometry}
                                // material={nodes.box_s7.material}
                                position={[-47.677, 2.305, 12.025]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s18.geometry}
                                // material={nodes.box_s18.material}
                                position={[-4.502, 2.305, 13.639]}
                                rotation={[0, Math.PI / 2, 0]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s19.geometry}
                                // material={nodes.box_s19.material}
                                position={[6.376, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s24.geometry}
                                // material={nodes.box_s24.material}
                                position={[28.259, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s25.geometry}
                                // material={nodes.box_s25.material}
                                position={[32.636, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s26.geometry}
                                // material={nodes.box_s26.material}
                                position={[36.468, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s27.geometry}
                                // material={nodes.box_s27.material}
                                position={[39.742, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s28.geometry}
                                // material={nodes.box_s28.material}
                                position={[43.021, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s29.geometry}
                                // material={nodes.box_s29.material}
                                position={[47.941, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_s30.geometry}
                                // material={nodes.box_s30.material}
                                position={[54.509, 2.305, 13.639]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i4.geometry}
                                // material={nodes.box_i4.material}
                                position={[-32.533, 2.635, 5.403]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i2.geometry}
                                // material={nodes.box_i2.material}
                                position={[-32.533, 2.635, -5.252]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i1.geometry}
                                // material={nodes.box_i1.material}
                                position={[-36.144, 2.933, -5.373]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i3.geometry}
                                // material={nodes.box_i3.material}
                                position={[-36.144, 2.933, 5.524]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i7.geometry}
                                // material={nodes.box_i7.material}
                                position={[-17.182, 1.356, 4.04]}
                                rotation={[-Math.PI, 1.044, -Math.PI]}
                                scale={[1.026, 1, 1.026]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i6.geometry}
                                // material={nodes.box_i6.material}
                                position={[-13.88, 1.356, -1.717]}
                                rotation={[Math.PI, -1.051, Math.PI]}
                                scale={[1.023, 1, 1.023]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i12.geometry}
                                // material={nodes.box_i12.material}
                                position={[15.47, 1.356, -1.943]}
                                scale={[1.148, 1, 1.148]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i14.geometry}
                                // material={nodes.box_i14.material}
                                position={[19.185, 1.356, 4.494]}
                                rotation={[-Math.PI, 1.044, -Math.PI]}
                                scale={[1.148, 1, 1.148]}
                                material={material}
                            />
                            <mesh
                                geometry={nodes.box_i13.geometry}
                                // material={nodes.box_i13.material}
                                position={[22.877, 1.356, -1.944]}
                                rotation={[Math.PI, -1.051, Math.PI]}
                                scale={[1.144, 1, 1.144]}
                                material={material}
                            />
                            <mesh
                                name="Technopark002"
                                geometry={nodes.Technopark002.geometry}
                                material={technoparkMaterial}
                                position={[1.363, 0.002, -21.454]}
                                scale={20}
                            />
                            <mesh
                                name="Technopark001"
                                geometry={nodes.Technopark001.geometry}
                                material={vhodMaterial}
                                position={[-42.83, 0.002, -21.454]}
                                scale={20}
                            />
                            <mesh
                                name="EXPO_Lounge001"
                                geometry={nodes.EXPO_Lounge001.geometry}
                                material={expoLoungeMaterial}
                                position={[1.189, 0.003, 18.011]}
                                scale={20}
                            />
                            <mesh
                                name="EXPO_Lounge"
                                geometry={nodes.EXPO_Lounge.geometry}
                                material={streetMaterial}
                                position={[58.808, 0.002, -0.087]}
                                rotation={[0, 1.571, 0]}
                                scale={20}
                            />
                            {/* --- */}
                            <group>
                                {Object.entries(pavilionsData).map(([name, pavilionData], i) => (
                                    <Html
                                        key={i}
                                        position={(positionsData as any)[`icon_${name}`]}
                                        distanceFactor={40}
                                        portal={markersPortalEl as React.MutableRefObject<HTMLElement>}
                                        className="pointer-events-auto stand-picker-button-wrapper fade-in"
                                    >
                                        {/* Preload images */}
                                        {pavilionData.image?.src && (
                                            <Image
                                                src={pavilionData.image.src}
                                                width={pavilionData.image.width}
                                                height={pavilionData.image.height}
                                                alt=""
                                                sizes="498px"
                                                fetchPriority="low"
                                                style={{ visibility: 'hidden' }}
                                            />
                                        )}
                                        <button
                                            className={classNames('text-s stand-picker-button js-pavilion', {
                                                'is-inactive':
                                                    type === 'unset'
                                                        ? false
                                                        : !(
                                                              type[0] === name[0] &&
                                                              (space !== 0 ? pavilionData.space === space : true)
                                                          ),
                                                'is-picked': pavilionData.slug === activePavilionSlug,
                                                'is-booked': !!pavilionData.booking,
                                            })}
                                            hidden={
                                                bookingType === 'booked'
                                                    ? !pavilionData.booking
                                                    : bookingType === 'available'
                                                      ? !!pavilionData.booking
                                                      : false
                                            }
                                            onClick={() => {
                                                setActivePavilionSlug(name);
                                                openPopup(STAND_POPUP_NAME);
                                            }}
                                        >
                                            {pavilionData.booking ? (
                                                <>
                                                    {pavilionData.booking.company.previewLogo?.src && (
                                                        <Image
                                                            src={pavilionData.booking.company.previewLogo.src}
                                                            className="img-fluid stand-picker-button-booked__logo"
                                                            width={pavilionData.booking.company.previewLogo.width}
                                                            height={pavilionData.booking.company.previewLogo.height}
                                                            alt={`Логотип ${pavilionData.booking.company.name}`}
                                                            sizes="100px"
                                                        />
                                                    )}
                                                    {/* <span className="stand-picker-button-booked__right">
                                                        <span className="text-s stand-picker-button-booked-name">
                                                            {pavilionData.booking.company.name}
                                                        </span>
                                                        <span className="text-xxs stand-picker-button-booked__status">
                                                            Стенд занят
                                                        </span>
                                                    </span> */}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="stand-picker-button__name">{name}</span>
                                                    {typeof pavilionData.space === 'number' && (
                                                        <span className="stand-picker-button__space">
                                                            {pavilionData.space} м<sup>2</sup>
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </button>
                                    </Html>
                                ))}
                            </group>
                        </Wrapper>
                    </ScrollControls>
                </group>
            </group>
        </>
    );
}

export default StandPicker;
